import { useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import Breadcrumbs from '@/components/Breadcrumbs/index.jsx'
import PageTitle from '@/components/PageTitle/index'
import Loader from '@/components/Loader'
import usePage from '@/hooks/use-page'
import SubmittalsListingPage from './SubmittalsListingPage'
import SubmittalsTabListing from './SubmittalsTabListing'
import useListingTabs from '@/hooks/useListingTabs'
import { LISTING_PATH, listingPathFor } from './listing-config'

const PAGE_TITLE = 'Submittals'

// The wording once the page is scoped to one data element. Not pitstop's "Submit
// data to be reviewed by external reviewers." — that is the submitter's voice,
// and the reader here is the reviewer.
const DATA_ELEMENT_SUBTITLE = 'Review and approve submissions from Main Contractors.'

const BASE_BREADCRUMBS = [
  { label: '', link: LISTING_PATH, isHomePage: true },
  { label: PAGE_TITLE, link: LISTING_PATH },
]

/**
 * The Submittals page, scoped to whichever data element the URL names.
 *
 * The header lives here rather than inside either listing: which listing renders
 * depends on the data element, so a header inside one would unmount when the user
 * switched and the whole page would appear to reload.
 *
 * The selection is the URL, not state — the sidebar is what switches between data
 * elements now, and state would leave a shared link, a refresh and the sidebar
 * highlight all disagreeing with the table on screen.
 */
const SubmittalsTabbedListingPage = () => {
  const { tabs, hasConfigTabs, isLoaded } = useListingTabs()
  const { dataElementId } = useParams()

  const activeTab = tabs.find(tab => tab.dataElementId === dataElementId)

  // usePage seeds `breadcrumbsItems` with useState, so what it returns is the
  // crumb list from this page's *first* render and nothing else. The page now
  // deliberately outlives a data element switch, so the crumbs are derived here
  // instead; usePage is still what sets document.title, which it does per render
  // and therefore does follow the switch.
  usePage({ title: activeTab?.label || PAGE_TITLE, breadcrumbs: BASE_BREADCRUMBS })

  const breadcrumbsItems = useMemo(() => {
    // Home and "Submittals" both return to whichever tab is open, not the bare
    // listing — the bare listing redirects to the first tab (below), which is
    // not necessarily the one the reader came from.
    const homeLink = activeTab ? listingPathFor(activeTab.dataElementId) : LISTING_PATH
    const base = [
      { label: '', link: homeLink, isHomePage: true },
      { label: PAGE_TITLE, link: homeLink },
    ]
    return activeTab && hasConfigTabs
      ? [...base, { label: activeTab.label, link: listingPathFor(activeTab.dataElementId) }]
      : base
  }, [activeTab, hasConfigTabs])

  // The tab list comes from the data element config. Rendering before it settles
  // would draw a configured table with no columns — and it has to precede the
  // redirect below, which would otherwise resolve "the first data element"
  // against a list that has not arrived.
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader />
      </div>
    )
  }

  // Every data element can disappear when its config is removed — render nothing
  // rather than a frame around no table at all.
  if (tabs.length === 0) return null

  // Nothing to select between: a single unscoped table under the bare /submittals
  // URL, with the plain heading. Scoping it would hide other data elements'
  // submittals with no way to reach them, and there is no sidebar sub-item to
  // reach them from either.
  if (!hasConfigTabs) {
    return (
      <>
        <Breadcrumbs breadcrumbItems={breadcrumbsItems} />
        <PageTitle pageTitle={PAGE_TITLE} />
        <SubmittalsListingPage
          embedded
          dataElementId={null}
          secondaryApproval={tabs[0].secondaryApproval}
        />
      </>
    )
  }

  // Two ways to arrive with no data element selected: the bare /submittals, and a
  // de/ URL naming one that is gone. Both resolve to the first element the sidebar
  // offers, by redirect rather than by quietly rendering it, so the address bar
  // and the sidebar highlight agree with the table on screen.
  if (!activeTab) return <Navigate to={listingPathFor(tabs[0].dataElementId)} replace />

  return (
    <>
      <Breadcrumbs breadcrumbItems={breadcrumbsItems} />
      <PageTitle pageTitle={activeTab.label} subtitle={DATA_ELEMENT_SUBTITLE} />

      {activeTab.isV1 ? (
        <SubmittalsListingPage
          embedded
          dataElementId={activeTab.dataElementId}
          secondaryApproval={activeTab.secondaryApproval}
        />
      ) : (
        <SubmittalsTabListing
          // Not keyed by data element: both de/ URLs resolve to the same route
          // element, so a switch changes props without unmounting, and the search
          // term carries across. The sort and the filter selections — which name
          // columns and values the next data element may not have — are reset by
          // the listing itself.
          dataElementId={activeTab.dataElementId}
          columns={activeTab.columns}
          filters={activeTab.filters}
          secondaryApproval={activeTab.secondaryApproval}
        />
      )}
    </>
  )
}

export default SubmittalsTabbedListingPage
